/**
 * Provider Table Component
 *
 * Displays login providers with a test-connection action per row. The
 * `main-session` row is read-only (backend 409s on PUT/DELETE) so it hides
 * Edit and Delete, and is tagged "managed by deployment"; Bindings and Test
 * remain available since only the provider record itself is immutable.
 */

import { useState, type ReactElement } from 'react';
import type { Provider, TestConnection } from '../../types';
import { useTestLoginProvider, useDeleteLoginProvider } from '../../hooks/useAdminLoginProviders';

interface ProviderTableProps {
  providers: Provider[];
  onEdit: (provider: Provider) => void;
  onBindings: (provider: Provider) => void;
  onDelete: (provider: Provider) => void;
}

export function ProviderTable({
  providers,
  onEdit,
  onBindings,
  onDelete,
}: ProviderTableProps): ReactElement {
  const testProvider = useTestLoginProvider();
  const deleteProvider = useDeleteLoginProvider();
  const [testResults, setTestResults] = useState<Record<string, TestConnection>>({});
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleTest = (provider: Provider): void => {
    setTestingId(provider.id);
    testProvider.mutate(provider.id, {
      onSuccess: (result) => {
        setTestResults((prev) => ({ ...prev, [provider.id]: result }));
      },
      onError: () => {
        setTestResults((prev) => ({
          ...prev,
          [provider.id]: { ok: false, detail: 'Test request failed' },
        }));
      },
      onSettled: () => { setTestingId(null); },
    });
  };

  const handleDelete = (provider: Provider): void => {
    const gitServerWarning =
      provider.git_servers.length > 0
        ? ` This detaches git login for: ${provider.git_servers.join(', ')}.`
        : '';
    if (!window.confirm(`Delete "${provider.display_name}"?${gitServerWarning}`)) return;
    setDeleteError(null);
    setDeletingId(provider.id);
    deleteProvider.mutate(provider.id, {
      onSuccess: () => { onDelete(provider); },
      onError: () => { setDeleteError('Failed to delete provider.'); },
      onSettled: () => { setDeletingId(null); },
    });
  };

  return (
    <div className="provider-table">
      <table className="feature-table__table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Mode</th>
            <th>Enabled</th>
            <th>Secret</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {providers.length === 0 ? (
            <tr>
              <td colSpan={6} className="feature-table__empty">
                No login providers configured.
              </td>
            </tr>
          ) : (
            providers.map((provider) => {
              const isMainSession = provider.type === 'main_session';
              const result = testResults[provider.id];
              return (
                <tr key={provider.id}>
                  <td>
                    <div className="feature-table__name">{provider.display_name}</div>
                    <div className="feature-table__subtext">{provider.name}</div>
                    {isMainSession && (
                      <span
                        className="feature-table__badge"
                        title="Provider record is managed by deploy-time config"
                      >
                        managed by deployment
                      </span>
                    )}
                    {provider.git_servers.map((serverName) => (
                      <span
                        key={serverName}
                        className="feature-table__badge"
                        title="Deleting this provider detaches git auth for this server"
                      >
                        Git server: {serverName}
                      </span>
                    ))}
                  </td>
                  <td>{provider.type}</td>
                  <td>{provider.acquisition_mode ?? '—'}</td>
                  <td>
                    <span
                      className={`feature-table__toggle ${provider.enabled ? 'feature-table__toggle--active' : ''}`}
                    >
                      {provider.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>{provider.has_client_secret ? 'set' : '—'}</td>
                  <td>
                    <div className="feature-table__actions-cell">
                      {!isMainSession && (
                        <button
                          type="button"
                          className="feature-table__edit-btn"
                          onClick={() => { onEdit(provider); }}
                          aria-label={`Edit ${provider.display_name}`}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        type="button"
                        className="feature-table__edit-btn"
                        onClick={() => { onBindings(provider); }}
                        aria-label={`Manage bindings for ${provider.display_name}`}
                      >
                        Bindings
                      </button>
                      <button
                        type="button"
                        className="feature-table__edit-btn"
                        onClick={() => { handleTest(provider); }}
                        disabled={testingId === provider.id}
                        aria-label={`Test ${provider.display_name}`}
                      >
                        {testingId === provider.id ? 'Testing...' : 'Test'}
                      </button>
                      {!isMainSession && (
                        <button
                          type="button"
                          className="feature-table__edit-btn feature-table__edit-btn--danger"
                          onClick={() => { handleDelete(provider); }}
                          disabled={deletingId === provider.id}
                          aria-label={`Delete ${provider.display_name}`}
                        >
                          {deletingId === provider.id ? 'Deleting...' : 'Delete'}
                        </button>
                      )}
                    </div>
                    {result && (
                      <div
                        className={`provider-table__test-result ${result.ok ? 'provider-table__test-result--ok' : 'provider-table__test-result--error'}`}
                        role="status"
                      >
                        {result.detail}
                      </div>
                    )}
                    {deleteError && (
                      <div
                        className="provider-table__test-result provider-table__test-result--error"
                        role="alert"
                      >
                        {deleteError}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
